import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, MessageSquare, AlertTriangle, Send } from "lucide-react";

const STATUS_COLORS = {
  aberto: "bg-amber-100 text-amber-700 border-amber-200",
  respondido: "bg-blue-100 text-blue-700 border-blue-200",
  fechado: "bg-green-100 text-green-700 border-green-200",
};

const PRIORITY_COLORS = {
  baixa: "bg-stone-100 text-stone-700",
  media: "bg-amber-100 text-amber-700",
  alta: "bg-red-100 text-red-700",
};

const CATEGORY_LABELS = {
  bug: "Bug", duvida: "Dúvida", sugestao: "Sugestão", cobranca: "Cobrança", outro: "Outro"
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [closeAfter, setCloseAfter] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = status !== "all" ? `?status=${status}` : "";
      const { data } = await api.get(`/admin/tickets${params}`);
      setTickets(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const openTicket = async (t) => {
    const { data } = await api.get(`/admin/tickets/${t.id}`);
    setSelected(data);
    setReply(""); setCloseAfter(false);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/tickets/${selected.id}/reply`, { message: reply, close: closeAfter });
      toast.success(closeAfter ? "Resposta enviada e ticket fechado" : "Resposta enviada");
      setSelected(null);
      load();
    } catch (e) { toast.error("Erro ao responder"); }
    finally { setSending(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-5" data-testid="admin-tickets-root">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Suporte</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A2F]">Tickets</h1>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-tickets-filter" className="w-48"><SelectValue placeholder="Filtrar status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="aberto">Abertos</SelectItem>
            <SelectItem value="respondido">Respondidos</SelectItem>
            <SelectItem value="fechado">Fechados</SelectItem>
          </SelectContent>
        </Select>
      </header>

      {loading ? (
        <div className="text-stone-500">Carregando…</div>
      ) : tickets.length === 0 ? (
        <Card className="p-10 text-center text-stone-500 rounded-2xl">
          Nenhum ticket por aqui. 🎉
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <Card
              key={t.id}
              data-testid={`admin-ticket-card-${t.id}`}
              onClick={() => openTicket(t)}
              className="p-4 rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition border-stone-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#1B3A2F] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {t.user_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{t.subject}</span>
                    <Badge className={STATUS_COLORS[t.status]} variant="outline">{t.status}</Badge>
                    <Badge className={PRIORITY_COLORS[t.priority]} variant="outline">prio: {t.priority}</Badge>
                    <Badge variant="outline">{CATEGORY_LABELS[t.category] || t.category}</Badge>
                  </div>
                  <div className="text-xs text-stone-500">
                    {t.user_name} · {t.user_email} · {t.messages?.length || 0} mensagens · {new Date(t.last_activity_at).toLocaleString("pt-BR")}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> {selected.subject}
                </DialogTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className={STATUS_COLORS[selected.status]} variant="outline">{selected.status}</Badge>
                  <Badge className={PRIORITY_COLORS[selected.priority]} variant="outline">prio: {selected.priority}</Badge>
                  <Badge variant="outline">{CATEGORY_LABELS[selected.category]}</Badge>
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  De: <strong>{selected.user?.name}</strong> ({selected.user?.email}) · Plano {selected.user?.plan}
                </div>
              </DialogHeader>

              <div className="space-y-3 my-4">
                {selected.messages?.map((m) => (
                  <div key={m.id} className={`p-3 rounded-lg text-sm ${m.from === "user" ? "bg-stone-100" : "bg-[#1B3A2F]/8 border-l-4 border-[#D4651A]"}`}>
                    <div className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold mb-1">
                      {m.from === "user" ? "Corretor" : `Suporte (${m.admin_name})`} · {new Date(m.created_at).toLocaleString("pt-BR")}
                    </div>
                    <div className="whitespace-pre-wrap text-stone-700">{m.message}</div>
                  </div>
                ))}
              </div>

              {selected.status !== "fechado" && (
                <div className="space-y-3 border-t border-stone-100 pt-4">
                  <Textarea
                    data-testid="admin-ticket-reply-input"
                    rows={4}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Sua resposta..."
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox id="close-after" checked={closeAfter} onCheckedChange={setCloseAfter} />
                    <label htmlFor="close-after" className="text-sm cursor-pointer text-stone-600">Marcar como resolvido após enviar</label>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Fechar</Button>
                {selected.status !== "fechado" && (
                  <Button
                    data-testid="admin-send-reply-btn"
                    onClick={sendReply}
                    disabled={!reply.trim() || sending}
                    className="bg-[#1B3A2F] hover:bg-[#244A3C]"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {sending ? "Enviando..." : closeAfter ? "Responder e fechar" : "Responder"}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
